import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Loading, Button } from 'carbon-components-react';
import { ReferralsDataTable } from 'components';
import { connect, useDispatch, useSelector } from 'react-redux';
import { selectedEnquiry, selectEnquiryLoader } from 'redux/enquiries';
import { CFCProps, SCREEN_SIZE } from 'types';
import {
  getEnquiryStatusCodeMsg,
  getResponsesReceivedCount
} from 'appConstants/enquiry';
import { Forum20, NoImage32, ArrowUp16 } from '@carbon/icons-react';
import AutoRenewSection from './autoRenewSection';
import JobDateSection from './jobDateSection';
import MoreInfoPanel from './moreInfo';
import ReferenceSection from './referenceSection';
import { zoomToFeature } from 'components/Map/helper';
import { useHistory } from 'react-router-dom';
import {
  queryFeatureLayerFeaturesStart,
  clearFeatureLayerLayerFeatures
} from 'redux/map/actions';
import { RootState } from 'redux/root.reducer';
import { useComponentMount } from 'utilities/hooks.util';
import {
  previewEmailStart,
  previewEmailSuccess,
  downloadEmailBodyStart,
  clearEmailBodyDownload
} from 'redux/emails';
import { PreviewModal } from '../previewModal';
import { APP } from 'app/AppRoutes';
import { getEmailAttachment } from 'utilities/helper.util';

const propTypes = {
  isFullSet: PropTypes.bool,
  enquiryId: PropTypes.number,
  isListView: PropTypes.bool,
  onClickCloseButton: PropTypes.func
};

type ownProps = PropTypes.InferProps<typeof propTypes>;

export const EnquiryDetailsPanelBody: React.FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const {
    isListView,
    onClickCloseButton,
    isFullSet,
    selectedFeatures,
    screenSize,
    previewEmail,
    emailBodyForDownload,
    previewing
  } = props;
  const history = useHistory();

  const enquiry: any = useSelector(selectedEnquiry);
  const isDataLoading: boolean = useSelector(selectEnquiryLoader);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [miniMapUrl, setMiniMapUrl] = useState<string>('');
  const [responsesReceivedCount, setResponsesReceivedCount] = useState<number>(
    0
  );
  const [
    inboundEmailIdToDownload,
    setInboundEmailIdToDownload
  ] = useState<number>(0);
  const [responsesTotalCount, setResponsesTotalCount] = useState<number>(0);
  const [downloadButtonClicked, setDownloadButtonClicked] = useState<boolean>(
    false
  );
  const [isEMLNotAvailable, setIsEMLNotAvailable] = useState<boolean>(true);

  const onClickCloseButtonHandler = useCallback(() => {
    onClickCloseButton && onClickCloseButton();
  }, [onClickCloseButton]);
  const bodyDivRef = useRef<HTMLDivElement>(null);

  const goTopPosition = () => {
    if (bodyDivRef.current) {
      bodyDivRef.current.scrollTop = 0;
    }
  };

  const handleResponseClick = (
    outboundEmailId: number,
    inboundEmailId: number
  ) => {
    setInboundEmailIdToDownload(inboundEmailId);
    dispatch(previewEmailStart({ emailId: outboundEmailId }));
  };

  const handleClosePreview = () => {
    dispatch(previewEmailSuccess(undefined));
    setIsEMLNotAvailable(true);
    dispatch(clearEmailBodyDownload());
  };

  const onDownload = () => {
    setDownloadButtonClicked(true);
  };

  useEffect(() => {
    if (inboundEmailIdToDownload > 0) {
      dispatch(
        downloadEmailBodyStart({
          emailId: inboundEmailIdToDownload,
          direction: 'inbound',
          downloadEmail: true
        })
      );
    }
  }, [inboundEmailIdToDownload, dispatch]);

  useEffect(() => {
    if (emailBodyForDownload) {
      const attachment: any = emailBodyForDownload.Attachments.find(
        (file: any) => file.mimeType === 'message/rfc822'
      );
      attachment ? setIsEMLNotAvailable(false) : setIsEMLNotAvailable(true);
      if (downloadButtonClicked) {
        getEmailAttachment({
          url: attachment.url,
          attachmentName: attachment.name
        });
        setDownloadButtonClicked(false);
      }
    }
  }, [emailBodyForDownload, downloadButtonClicked]);

  useEffect(() => {
    if (enquiry) {
      const { referrals, MapImageFile } = enquiry;
      const { receivedCount, totalCount } = getResponsesReceivedCount(
        referrals
      );
      setReferrals(referrals);
      setResponsesReceivedCount(receivedCount);
      setResponsesTotalCount(totalCount);
      MapImageFile?.url && setMiniMapUrl(MapImageFile.url);
    }
  }, [enquiry]);

  useEffect(() => {
    if (
      selectedFeatures &&
      Array.isArray(selectedFeatures) &&
      selectedFeatures.length > 0
    ) {
      zoomToFeature(selectedFeatures[0].geometry.extent, 0, true);
    }
  }, [selectedFeatures]);

  useComponentMount(() => {
    return () => {
      dispatch(clearFeatureLayerLayerFeatures());
    };
  });

  const cloneExistingEnquiryHandler = useCallback(
    (enquiryId: number) => {
      history.push(`/enquiries/new?clone=${enquiryId}`);
    },
    [history]
  );

  const zoomToMap = useCallback(
    (enquiryId: number) => {
      const { loadedMap } = window;
      const enquiriesFL = loadedMap.findLayerById('enquiries-feature-layer');
      const queryParams = {
        objectIds: [enquiryId],
        returnGeometry: true
      };
      dispatch(queryFeatureLayerFeaturesStart(enquiriesFL, queryParams));
    },
    [dispatch]
  );

  return (
    <div
      ref={bodyDivRef}
      id="enquiryRightPanelBody"
      data-testid="enqiryRightPanelBody"
      className={`panel--body${isFullSet ? ' custom-scroller' : ''}`}
    >
      {isDataLoading ? (
        <div className="loader--wrapper">
          <Loading description="Active loading indicator" withOverlay={false} />
        </div>
      ) : (
        <>
          <h2
            className={`panel--body-sub-heading${!isListView ? ' no-map' : ''}`}
          >
            {isFullSet ? (
              <>
                Responses received{' '}
                <span className="received-count-text">
                  {responsesReceivedCount}
                </span>
                /{responsesTotalCount}
              </>
            ) : (
              getEnquiryStatusCodeMsg(enquiry?.status)
            )}
          </h2>
          {!isFullSet && (
            <div className="panel--body-job-date">
              <JobDateSection
                id={enquiry?.id}
                isAutoRenewComplete={enquiry?.isAutoRenewComplete}
                autoRenewUntil={enquiry?.autoRenewUntil}
                digStartAt={enquiry?.digStartAt}
                digEndAt={enquiry?.digEndAt}
              />
            </div>
          )}
          {(isListView || screenSize === SCREEN_SIZE.SM) && (
            <div className="panel--body-map">
              {miniMapUrl ? <img src={miniMapUrl} alt="" /> : <NoImage32 />}
            </div>
          )}
          <div className="panel--body-eDetails">
            {referrals && (
              <ReferralsDataTable
                isLoading={isDataLoading && referrals?.length === 0}
                referrals={referrals}
                handleResponseClick={handleResponseClick}
                isFullSet={isFullSet || false}
                showResponsePreview={true}
              />
            )}
          </div>
          {!isFullSet && (
            <>
              <div className="panel--body-reference">
                <ReferenceSection originalEnquiry={enquiry} />
              </div>
              <div className="panel--body-auto-renew">
                <AutoRenewSection
                  id={enquiry?.id}
                  isAutoRenewComplete={enquiry?.isAutoRenewComplete}
                  autoRenewUntil={enquiry?.autoRenewUntil}
                  digEndAt={enquiry?.digEndAt}
                  createdAt={enquiry?.createdAt}
                />
              </div>
            </>
          )}
          {isFullSet && (
            <>
              <div className="panel--body-go-top-button">
                <Button
                  renderIcon={ArrowUp16}
                  kind="ghost"
                  onClick={() => goTopPosition()}
                >
                  Top
                </Button>
              </div>
              <MoreInfoPanel originalEnquiry={enquiry} />
              <div className="panel--body-go-top-button">
                <Button
                  renderIcon={ArrowUp16}
                  kind="ghost"
                  onClick={() => goTopPosition()}
                >
                  Top
                </Button>
                <Button
                  kind="ghost"
                  onClick={() => onClickCloseButtonHandler()}
                >
                  Close
                </Button>
              </div>
            </>
          )}
          <div className="panel--body-bottom">
            {!isFullSet && (
              <>
                <div className="panel--body-clone-enquiry">
                  <div className="panel--body-clone-enquiry-left">
                    <Button
                      className="action--button"
                      size="small"
                      kind="tertiary"
                      onClick={() => cloneExistingEnquiryHandler(enquiry?.id)}
                    >
                      Duplicate
                    </Button>
                    {!isListView && screenSize !== SCREEN_SIZE.SM && (
                      <Button
                        className="action--button"
                        size="small"
                        kind="tertiary"
                        onClick={() => zoomToMap(enquiry?.id)}
                      >
                        Zoom map
                      </Button>
                    )}
                  </div>
                  <Button
                    className="action--button"
                    size="small"
                    kind="ghost"
                    onClick={() => onClickCloseButtonHandler()}
                  >
                    Close
                  </Button>
                </div>
                <hr className="section--divider" />
              </>
            )}
            <div className="panel--body-bottomContent">
              <Forum20 />
              <ul>
                <li>Typical wait times are approximate</li>
                <li>Sometimes members must be called</li>
                <li>Some asset owners are not yet members</li>
              </ul>
            </div>
          </div>
        </>
      )}

      {previewing && <Loading withOverlay={true} />}

      {previewEmail &&
        ReactDOM.createPortal(
          <PreviewModal
            email={previewEmail}
            onClose={() => handleClosePreview()}
            onDownload={onDownload}
            isEMLNotAvailable={isEMLNotAvailable}
          />,
          document.querySelector(`#${APP}`) || document.body
        )}
    </div>
  );
};

EnquiryDetailsPanelBody.propTypes = propTypes;

EnquiryDetailsPanelBody.defaultProps = {
  enquiryId: undefined,
  isListView: false,
  onClickCloseButton: () => {}
};

const mapStateToProps = ({ mapCache, session, emailCache }: RootState) => ({
  selectedFeatures: mapCache.featureLayerfeatures,
  screenSize: session.screenSize,
  previewEmail: emailCache.emailPreview,
  emailBodyForDownload: emailCache.emailBodyDownload,
  previewing: emailCache.previewing
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector> & ownProps;

export default React.memo(connector(EnquiryDetailsPanelBody));
